/**
 * ISO9660 reader (ECMA-119).
 *
 * Parses:
 *   - Primary Volume Descriptor at sector 16 (PVD, type 1)
 *   - Supplementary Volume Descriptor (SVD, type 2 — Joliet) — overlay applied separately
 *   - Boot Record VD (type 0 — El Torito) — parsed separately by el-torito-boot.js
 *   - Directory record tree from PVD root
 *
 * Cross-cutting: never holds the whole image. All reads go through BinaryReader.
 *
 * Surface: window.__DiskImage.Iso9660
 *   var iso = new Iso9660(reader);
 *   await iso.mount() → { pvd, root, svdLba?, brLba? }
 *   iso.tree() → root Node
 *   iso.streamFile(node) → ReadableStream
 *
 * Node shape: {
 *   name,                // ASCII name (Joliet/RockRidge overlays may add unicodeName/posixName)
 *   path,                // '/dir/file' POSIX-style
 *   isDir,
 *   lba,                 // sector at which extent starts (0 for synthetic root)
 *   size,                // data length in bytes
 *   recDate?,            // ISO9660 dir record date (7 bytes)
 *   flags,               // raw flags byte
 *   children: Node[],    // for directories, populated lazily by listDir()
 *   _systemUse?: Uint8Array,  // raw SUSP area for Rock Ridge overlay
 *   _parent?: Node
 * }
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.Iso9660) return;

    var errors = w.__DiskImage.errors || {};
    var CorruptedImageError = errors.CorruptedImageError || function (m, d) { var e = new Error(m); e.name = 'CorruptedImageError'; e.details = d; return e; };

    var SECTOR = 2048;

    function _u32leAt(b, off) {
        return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0);
    }
    function _u16leAt(b, off) {
        return (b[off] | (b[off + 1] << 8));
    }

    function _ascii(b, off, len) {
        var s = '';
        for (var i = 0; i < len; i++) {
            var c = b[off + i];
            if (c === 0) break;
            s += String.fromCharCode(c);
        }
        return s.replace(/\s+$/, '');
    }

    function _decodeRecDate(b, off) {
        // 7-byte ISO9660 directory record date: years from 1900, month, day, hour, min, sec, GMT-offset (15-min units)
        var y = b[off] + 1900;
        var mo = b[off + 1];
        var d = b[off + 2];
        var hh = b[off + 3];
        var mm = b[off + 4];
        var ss = b[off + 5];
        var gmt = (b[off + 6] << 24) >> 24; // signed
        if (!y && !mo && !d) return null;
        return { y: y, mo: mo, d: d, hh: hh, mm: mm, ss: ss, gmtOffset15min: gmt };
    }

    function Iso9660(reader) {
        this.reader = reader;
        this.pvd = null;
        this.root = null;
        this.svdLba = null;
        this.svdRoot = null;
        this.brLba = null;
    }

    Iso9660.prototype.mount = function () {
        var self = this;
        var sectorsRead = [];
        // Walk volume descriptor sequence starting at sector 16
        function readVd(lba) {
            return self.reader.bytes(lba * SECTOR, SECTOR).then(function (b) {
                var type = b[0];
                var stdId = String.fromCharCode(b[1], b[2], b[3], b[4], b[5]);
                if (stdId !== 'CD001') {
                    if (sectorsRead.length === 0) {
                        throw CorruptedImageError('PVD signature missing at sector 16', { stdId: stdId });
                    }
                    return; // outside the sequence
                }
                sectorsRead.push({ lba: lba, type: type });
                if (type === 0xFF) return; // terminator
                if (type === 1 && !self.pvd) {
                    self.pvd = _parsePvd(b, lba);
                    self.root = self._buildSyntheticRoot(self.pvd);
                } else if (type === 2 && !self.svdLba) {
                    // SVD — Joliet candidate
                    self.svdLba = lba;
                    self.svdParsed = _parsePvd(b, lba);
                } else if (type === 0 && !self.brLba) {
                    self.brLba = lba;
                    self.brSector = b;
                }
                return readVd(lba + 1);
            });
        }
        return readVd(16).then(function () {
            if (!self.pvd) throw CorruptedImageError('No Primary Volume Descriptor in volume recognition sequence');
            return self.populate(self.root);
        }).then(function () {
            return {
                pvd: self.pvd,
                root: self.root,
                svdLba: self.svdLba,
                brLba: self.brLba
            };
        });
    };

    Iso9660.prototype._buildSyntheticRoot = function (pvd) {
        return {
            name: '',
            path: '/',
            isDir: true,
            lba: pvd.rootLba,
            size: pvd.rootSize,
            flags: 0x02,
            children: [],
            recDate: pvd.rootRecDate,
            _parent: null,
            _systemUse: null
        };
    };

    function _parsePvd(b, lba) {
        var pvd = {
            type: b[0],
            stdId: _ascii(b, 1, 5),
            version: b[6],
            systemId: _ascii(b, 8, 32),
            volumeId: _ascii(b, 40, 32),
            volumeSpaceSize: _u32leAt(b, 80),
            escapeSeq: _ascii(b, 88, 32),       // for SVD: identifies UCS-2 level
            volumeSetSize: _u16leAt(b, 120),
            volumeSeqNum: _u16leAt(b, 124),
            logicalBlockSize: _u16leAt(b, 128),
            pathTableSize: _u32leAt(b, 132),
            pathTableLLba: _u32leAt(b, 140),
            pathTableMLba: _u32leAt(b, 148),
            volumeSetId: _ascii(b, 190, 128),
            publisherId: _ascii(b, 318, 128),
            preparerId: _ascii(b, 446, 128),
            applicationId: _ascii(b, 574, 128),
            copyrightFileId: _ascii(b, 702, 37),
            abstractFileId: _ascii(b, 739, 37),
            biblioFileId: _ascii(b, 776, 37),
            atSector: lba
        };
        // Root directory record at offset 156 (33 bytes + 1 byte filename = 34 total)
        pvd.rootLba = _u32leAt(b, 156 + 2);
        pvd.rootSize = _u32leAt(b, 156 + 10);
        pvd.rootRecDate = _decodeRecDate(b, 156 + 18);
        return pvd;
    }

    /**
     * Walk a directory's extent and append children.
     * Reads sector(s) starting at node.lba for node.size bytes, parses
     * directory records, recurses into subdirs.
     */
    Iso9660.prototype.populate = function (node, opts) {
        opts = opts || {};
        var maxDepth = opts.maxDepth != null ? opts.maxDepth : 32;
        var seen = opts._seen || new Set();
        if (!node.isDir) return Promise.resolve(node);
        if (seen.has(node.lba)) return Promise.resolve(node); // cycle guard
        seen.add(node.lba);
        if (node.size === 0) return Promise.resolve(node);

        var reader = this.reader;
        var self = this;
        return reader.bytes(node.lba * SECTOR, node.size).then(function (b) {
            var off = 0;
            // First two records are "." and ".." — skip those
            var skipped = 0;
            // A file over 4 GiB is several records with one identifier, all but
            // the last flagged 0x80 (ECMA-119 9.1.6, "multi-extent"). Read one by
            // one they listed a 5 GB file as two partial files; they are merged
            // into one node whose `extents` the readers below follow.
            var open = null;
            while (off < b.length) {
                var lenDr = b[off];
                if (lenDr === 0) {
                    // Pad to next sector boundary
                    var nextSector = Math.ceil((off + 1) / SECTOR) * SECTOR;
                    off = nextSector;
                    continue;
                }
                if (lenDr < 33) {
                    // malformed
                    break;
                }
                if (skipped < 2) { skipped++; off += lenDr; continue; }

                var rec = _parseDirRecord(b, off);
                if (rec && open && rec._rawIso9660Name === open._rawIso9660Name) {
                    open.extents.push({ lba: rec.lba, size: rec.size });
                    open.size += rec.size;
                    if (!(rec.flags & 0x80)) { open.flags = rec.flags; open = null; }
                    off += lenDr;
                    continue;
                }
                open = null;
                if (rec && !rec.isDir && (rec.flags & 0x80)) {
                    rec.extents = [{ lba: rec.lba, size: rec.size }];
                    open = rec;
                }
                if (rec) {
                    rec._parent = node;
                    rec.path = (node.path === '/' ? '/' : node.path + '/') + rec.name;
                    if (rec.isDir) {
                        rec.children = [];
                    }
                    node.children.push(rec);
                }
                off += lenDr;
            }
            // Recurse into subdirs (depth-first)
            if (maxDepth > 0) {
                var dirs = node.children.filter(function (c) { return c.isDir; });
                var p = Promise.resolve();
                dirs.forEach(function (d) {
                    p = p.then(function () {
                        return self.populate(d, { maxDepth: maxDepth - 1, _seen: seen });
                    });
                });
                return p;
            }
        }).then(function () {
            return node;
        });
    };

    function _parseDirRecord(b, off) {
        var lenDr = b[off];
        var extAttrLen = b[off + 1];
        var lba = _u32leAt(b, off + 2);
        var dataLen = _u32leAt(b, off + 10);
        var flags = b[off + 25];
        var lenFi = b[off + 32];
        var nameStart = off + 33;
        var nameEnd = nameStart + lenFi;

        // Convention: ID 0x00 = "." (current dir), 0x01 = ".."
        var name = '';
        var rawName = b.subarray(nameStart, nameEnd);
        if (lenFi === 1 && (b[nameStart] === 0x00 || b[nameStart] === 0x01)) {
            return null; // dot/dotdot — skip
        } else {
            for (var i = 0; i < lenFi; i++) name += String.fromCharCode(b[nameStart + i]);
            // ISO9660 file IDs end with ;1 — strip
            var semi = name.lastIndexOf(';');
            if (semi >= 0) name = name.slice(0, semi);
            // Trailing dot
            if (name.endsWith('.')) name = name.slice(0, -1);
        }

        // Padding byte if lenFi is even
        var sysUseStart = nameEnd + (lenFi % 2 === 0 ? 1 : 0);
        var sysUse = (sysUseStart < off + lenDr) ? b.slice(sysUseStart, off + lenDr) : null;

        var recDate = _decodeRecDate(b, off + 18);

        return {
            name: name,
            isDir: !!(flags & 0x02),
            lba: lba,
            size: dataLen,
            extAttrLen: extAttrLen,
            flags: flags,
            recDate: recDate,
            children: null,
            _systemUse: sysUse,
            _rawIso9660Name: name
        };
    }

    Iso9660.prototype.streamFile = function (node) {
        if (!node || node.isDir) throw new TypeError('Iso9660.streamFile: node is not a file');
        if (!node.extents || node.extents.length < 2) return this.reader.streamRange(node.lba * SECTOR, node.size);
        // Multi-extent: the sections one after another, each streamed in turn.
        var reader = this.reader, exts = node.extents.slice(), cur = null;
        return new ReadableStream({
            pull: function (ctl) {
                function next() {
                    if (!cur) {
                        if (!exts.length) { ctl.close(); return; }
                        var e = exts.shift();
                        cur = reader.streamRange(e.lba * SECTOR, e.size).getReader();
                    }
                    return cur.read().then(function (r) {
                        if (r.done) { cur = null; return next(); }
                        ctl.enqueue(r.value);
                    });
                }
                return next();
            },
            cancel: function (why) { if (cur) return cur.cancel(why); }
        });
    };

    Iso9660.prototype.bytesOf = function (node, off, len) {
        if (off == null) off = 0;
        if (len == null) len = node.size - off;
        if (off + len > node.size) len = node.size - off;
        if (len <= 0) return Promise.resolve(new Uint8Array(0));
        if (!node.extents || node.extents.length < 2) return this.reader.bytes(node.lba * SECTOR + off, len);
        var reader = this.reader, parts = [], pos = 0, end = off + len;
        node.extents.forEach(function (e) {
            var s = Math.max(off, pos), t = Math.min(end, pos + e.size);
            if (t > s) parts.push(reader.bytes(e.lba * SECTOR + (s - pos), t - s));
            pos += e.size;
        });
        return Promise.all(parts).then(function (bufs) {
            var out = new Uint8Array(len), o = 0;
            bufs.forEach(function (b) { out.set(b, o); o += b.length; });
            return out;
        });
    };

    /**
     * Mount the SVD as a parallel tree (Joliet entry point).
     * Returns a synthetic root whose children carry UCS-2BE names.
     * joliet-overlay.js calls this internally.
     */
    Iso9660.prototype.mountSvd = function () {
        if (!this.svdParsed) return Promise.resolve(null);
        var parsed = this.svdParsed;
        var root = {
            name: '',
            path: '/',
            isDir: true,
            lba: parsed.rootLba,
            size: parsed.rootSize,
            flags: 0x02,
            children: [],
            _parent: null,
            _systemUse: null,
            _isJoliet: true
        };
        return this.populate(root).then(function () { return root; });
    };

    /**
     * Iterator yielding every node in the tree (depth-first).
     */
    Iso9660.prototype.iterate = function () {
        var nodes = [];
        function walk(n) {
            nodes.push(n);
            if (n.children) n.children.forEach(walk);
        }
        if (this.root) this.root.children.forEach(walk);
        return nodes;
    };

    Iso9660.prototype.findByPath = function (path) {
        if (!path || path === '/') return this.root;
        var parts = path.replace(/^\/+/, '').split('/');
        var node = this.root;
        for (var i = 0; i < parts.length; i++) {
            if (!node || !node.children) return null;
            var p = parts[i];
            var found = null;
            for (var j = 0; j < node.children.length; j++) {
                var c = node.children[j];
                if (c.name === p || c.unicodeName === p || c.posixName === p) { found = c; break; }
            }
            if (!found) return null;
            node = found;
        }
        return node;
    };

    w.__DiskImage.Iso9660 = Iso9660;
})(window);
